import React, { useCallback, useEffect, useState, createElement, Component } from 'react';
import { Building2, Key, Activity, Plus, ArrowLeft, Trash2, Save, AlertCircle, CheckCircle2, Server, ShieldAlert, Loader2, LogIn, LogOut, RefreshCw, Search, XCircle, Bug, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { WorkiumLogo } from '../components/brand/WorkiumLogo';
import { listTenants, getTenantWithSecrets, createTenant, updateTenant, deleteTenant, createOrUpdateSecret, deleteSecret, checkHealth, Tenant, TenantSecret } from '../services/tenantApi';
// ─── JWT Decode Helper ──────────────────────────────────────────────────────────
function parseJwt(token: string): Record<string, any> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}
// ─── Cognito Auth for Superadmin ────────────────────────────────────────────────
const COGNITO_ENDPOINT = 'https://cognito-idp.us-east-1.amazonaws.com/';
const COGNITO_CLIENT_ID = '60ps0o9uijosjnvim9uloiiggm';
const SA_TOKEN_KEY = 'sa_id_token';
const SA_TOKEN_EXPIRY_KEY = 'sa_token_expiry';
const SA_EMAIL_KEY = 'sa_email';
async function cognitoLogin(email: string, password: string): Promise<{
  token: string;
  expiresIn: number;
  email: string;
  role: string;
} | {
  error: string;
}> {
  try {
    const res = await fetch(COGNITO_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: COGNITO_CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password
        }
      })
    });
    const data = await res.json();
    if (data.AuthenticationResult?.IdToken) {
      const claims = parseJwt(data.AuthenticationResult.IdToken);
      const role = claims?.['custom:ROLE'] || claims?.['custom:role'] || '';
      const tokenEmail = claims?.email || email;
      // Validate SUPERADMIN role
      if (role.toUpperCase() !== 'SUPERADMIN') {
        return {
          error: `Acceso denegado. Tu rol es "${role || 'sin rol'}". Se requiere SUPERADMIN.`
        };
      }
      return {
        token: data.AuthenticationResult.IdToken,
        expiresIn: data.AuthenticationResult.ExpiresIn,
        email: tokenEmail,
        role
      };
    }
    return {
      error: data.message || data.__type || 'Authentication failed'
    };
  } catch (err: any) {
    return {
      error: err.message || 'Network error'
    };
  }
}
function getStoredSAToken(): string | null {
  const token = sessionStorage.getItem(SA_TOKEN_KEY);
  const expiry = sessionStorage.getItem(SA_TOKEN_EXPIRY_KEY);
  if (token && expiry && Date.now() < parseInt(expiry)) return token;
  sessionStorage.removeItem(SA_TOKEN_KEY);
  sessionStorage.removeItem(SA_TOKEN_EXPIRY_KEY);
  sessionStorage.removeItem(SA_EMAIL_KEY);
  return null;
}
function getStoredSAEmail(): string {
  return sessionStorage.getItem(SA_EMAIL_KEY) || 'superadmin';
}
function storeSASession(token: string, expiresIn: number, email: string) {
  sessionStorage.setItem(SA_TOKEN_KEY, token);
  sessionStorage.setItem(SA_TOKEN_EXPIRY_KEY, (Date.now() + expiresIn * 1000 - 60000).toString());
  sessionStorage.setItem(SA_EMAIL_KEY, email);
}
function clearSASession() {
  sessionStorage.removeItem(SA_TOKEN_KEY);
  sessionStorage.removeItem(SA_TOKEN_EXPIRY_KEY);
  sessionStorage.removeItem(SA_EMAIL_KEY);
}
// ─── Types ──────────────────────────────────────────────────────────────────────
type ViewState = 'list' | 'detail';
interface HealthData {
  ok: boolean;
  data?: any;
  error?: string;
  checkedAt?: string;
}
// ─── Login Gate ─────────────────────────────────────────────────────────────────
function SuperadminLogin({
  onAuth
}: {
  onAuth: (token: string, email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Ingresá email y contraseña');
      return;
    }
    setIsLoading(true);
    setError('');
    const result = await cognitoLogin(email, password);
    if ('error' in result) {
      setError(result.error);
      setIsLoading(false);
    } else {
      storeSASession(result.token, result.expiresIn, result.email);
      onAuth(result.token, result.email);
    }
  };
  return <div className="min-h-screen bg-slate-950 flex items-center justify-center relative overflow-hidden font-sans" data-id="element-6925">
      <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-red-900/10 blur-[120px] rounded-full pointer-events-none" data-id="element-6926" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-indigo-900/10 blur-[120px] rounded-full pointer-events-none" data-id="element-6927" />

      <div className="w-full max-w-sm px-6 relative z-10" data-id="element-6928">
        <div className="flex flex-col items-center mb-8" data-id="element-6929">
          <WorkiumLogo size="lg" className="mb-3" data-id="element-6930" />
          <Badge variant="error" className="mt-2" data-id="element-6931">
            Superadmin Console
          </Badge>
        </div>

        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl p-6 shadow-2xl" data-id="element-6932">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-6933">
            <div data-id="element-6934">
              <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1" data-id="element-6935">
                Email
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="superadmin@workium.ia" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50" autoFocus data-id="element-6936" />
            </div>
            <div data-id="element-6937">
              <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1" data-id="element-6938">
                Password
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50" data-id="element-6939" />
            </div>

            {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2 text-red-400 text-xs" data-id="element-6940">
                <AlertCircle size={14} className="shrink-0 mt-0.5" data-id="element-6941" />
                <span data-id="element-6942">{error}</span>
              </div>}

            <Button type="submit" className="w-full gap-2 bg-red-600 hover:bg-red-500 focus:ring-red-500" isLoading={isLoading} data-id="element-6943">
              <LogIn size={16} data-id="element-6944" />
              Acceder como Superadmin
            </Button>
          </form>
        </div>

        <p className="text-center text-[11px] text-slate-600 mt-6 font-mono" data-id="element-6945">
          Acceso restringido · Workium AI Internal
        </p>
      </div>
    </div>;
}
// ─── Main Superadmin Panel ──────────────────────────────────────────────────────
export function SuperadminPage() {
  const [token, setToken] = useState<string | null>(() => getStoredSAToken());
  const [email, setEmail] = useState(() => getStoredSAEmail());
  if (!token) {
    return <SuperadminLogin onAuth={(t, e) => {
      setToken(t);
      setEmail(e);
    }} data-id="element-6946" />;
  }
  return <SuperadminDashboard token={token} email={email} onLogout={() => {
    clearSASession();
    setToken(null);
  }} data-id="element-6947" />;
}
function SuperadminDashboard({
  token,
  email,
  onLogout
}: {
  token: string;
  email: string;
  onLogout: () => void;
}) {
  const [view, setView] = useState<ViewState>('list');
  const [activeTab, setActiveTab] = useState<'tenants' | 'health'>('tenants');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [tenantSecrets, setTenantSecrets] = useState<TenantSecret[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Debug
  const [showDebug, setShowDebug] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<any>(null);
  // Health
  const [health, setHealth] = useState<HealthData | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  // Form states
  const [isNewTenant, setIsNewTenant] = useState(false);
  const [formData, setFormData] = useState<Partial<Tenant>>({});
  const [secretForm, setSecretForm] = useState<{
    service: string;
    fields: Record<string, string>;
  }>({
    service: '',
    fields: {}
  });
  // Service → credential fields mapping
  const serviceFieldDefs: Record<string, {
    key: string;
    label: string;
    placeholder: string;
    type?: string;
  }[]> = {
    jira: [{
      key: 'baseUrl',
      label: 'Base URL',
      placeholder: 'https://tu-empresa.atlassian.net'
    }, {
      key: 'email',
      label: 'Email',
      placeholder: 'admin@empresa.com',
      type: 'email'
    }, {
      key: 'apiToken',
      label: 'API Token',
      placeholder: 'Token de Atlassian',
      type: 'password'
    }],
    confluence: [{
      key: 'baseUrl',
      label: 'Base URL',
      placeholder: 'https://tu-empresa.atlassian.net/wiki'
    }, {
      key: 'email',
      label: 'Email',
      placeholder: 'admin@empresa.com',
      type: 'email'
    }, {
      key: 'apiToken',
      label: 'API Token',
      placeholder: 'Token de Atlassian',
      type: 'password'
    }],
    slack: [{
      key: 'botToken',
      label: 'Bot Token',
      placeholder: 'xoxb-...',
      type: 'password'
    }, {
      key: 'signingSecret',
      label: 'Signing Secret',
      placeholder: 'Signing secret de la app',
      type: 'password'
    }],
    github: [{
      key: 'personalAccessToken',
      label: 'Personal Access Token',
      placeholder: 'ghp_...',
      type: 'password'
    }, {
      key: 'org',
      label: 'Organización',
      placeholder: 'mi-org'
    }],
    notion: [{
      key: 'integrationToken',
      label: 'Integration Token',
      placeholder: 'secret_...',
      type: 'password'
    }],
    google: [{
      key: 'clientId',
      label: 'Client ID',
      placeholder: '123456.apps.googleusercontent.com'
    }, {
      key: 'clientSecret',
      label: 'Client Secret',
      placeholder: 'GOCSPX-...',
      type: 'password'
    }, {
      key: 'refreshToken',
      label: 'Refresh Token',
      placeholder: '1//...',
      type: 'password'
    }]
  };
  const handleSecretFieldChange = (key: string, value: string) => {
    setSecretForm(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [key]: value
      }
    }));
  };
  const handleServiceChange = (service: string) => {
    setSecretForm({
      service,
      fields: {}
    });
  };
  const getSecretFieldsFilled = () => {
    const defs = serviceFieldDefs[secretForm.service];
    if (!defs) return false;
    return defs.every(d => (secretForm.fields[d.key] || '').trim() !== '');
  };
  const buildCredentialsJson = () => {
    const defs = serviceFieldDefs[secretForm.service];
    if (!defs) return {};
    const creds: Record<string, string> = {};
    defs.forEach(d => {
      if (secretForm.fields[d.key]) creds[d.key] = secretForm.fields[d.key];
    });
    return creds;
  };
  const downloadSecretJson = () => {
    const creds = buildCredentialsJson();
    const blob = new Blob([JSON.stringify(creds, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${secretForm.service || 'secret'}-credentials.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  // Auto-clear success messages
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);
  // Fetch tenants on mount and tab change
  useEffect(() => {
    if (activeTab === 'tenants' && view === 'list') {
      fetchTenants();
    }
    if (activeTab === 'health' && !health) {
      fetchHealth();
    }
  }, [activeTab, view]);
  const fetchTenants = async () => {
    setIsLoading(true);
    setError(null);
    const res = await listTenants(token);
    setLastApiResponse({
      endpoint: 'GET /tenants',
      ...res
    });
    if (res.error) {
      if (res.status === 401 || res.status === 403) {
        setError(`Sesión expirada o sin permisos (${res.status}). Re-logueate.`);
        onLogout();
        return;
      }
      setError(res.error);
    } else {
      // Handle different response shapes from the API:
      // Direct array: [...]
      // Wrapped: { data: [...] } or { tenants: [...] } or { items: [...] }
      // Double-wrapped: { success: true, data: [...] }
      const rawData = res.data as any;
      const extracted = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : Array.isArray(rawData?.tenants) ? rawData.tenants : Array.isArray(rawData?.items) ? rawData.items : null;
      if (extracted) {
        setTenants(extracted);
      } else {
        setTenants([]);
        console.warn('[Superadmin] Unexpected response shape:', rawData);
      }
    }
    setIsLoading(false);
  };
  const fetchHealth = async () => {
    setHealthLoading(true);
    const res = await checkHealth();
    setHealth({
      ...res,
      checkedAt: new Date().toLocaleTimeString()
    });
    setHealthLoading(false);
  };
  const handleViewTenant = async (tenant: Tenant) => {
    setIsLoading(true);
    setError(null);
    const data = await getTenantWithSecrets(tenant.tenant_id, token);
    setLastApiResponse({
      endpoint: `GET /tenants/${tenant.tenant_id} + secrets`,
      ...data
    });
    // Unwrap tenant from { success, data: {...} } wrapper
    const rawTenant = data.tenant as any;
    const unwrappedTenant: Tenant | null = rawTenant?.data && !Array.isArray(rawTenant.data) ? rawTenant.data : rawTenant;
    // Unwrap secrets from { success, data: [...] } wrapper
    const rawSecrets = data.secrets as any;
    const unwrappedSecrets: TenantSecret[] = Array.isArray(rawSecrets) ? rawSecrets : Array.isArray(rawSecrets?.data) ? rawSecrets.data : [];
    // Always show tenant detail — even if secrets fail
    setSelectedTenant(unwrappedTenant || tenant);
    setFormData(unwrappedTenant || tenant);
    setTenantSecrets(unwrappedSecrets);
    setIsNewTenant(false);
    setView('detail');
    // Show secrets error as a non-blocking warning (not the main error)
    if (data.error) {
      // Only show error if tenant also failed
      if (!unwrappedTenant) {
        setError(data.error);
      } else {
        // Secrets failed but tenant loaded — show as warning in success
        setSuccess(`⚠️ Secrets: ${data.error} (el endpoint de secrets puede requerir permisos adicionales)`);
      }
    }
    setIsLoading(false);
  };
  const handleCreateNew = () => {
    setSelectedTenant(null);
    setTenantSecrets([]);
    setFormData({
      name: '',
      industry: '',
      email: '',
      active_plan: 'Free'
    });
    setIsNewTenant(true);
    setError(null);
    setView('detail');
  };
  const handleSaveTenant = async () => {
    setIsSaving(true);
    setError(null);
    if (isNewTenant) {
      const res = await createTenant({
        name: formData.name || '',
        industry: formData.industry,
        email: formData.email || ''
      }, token);
      setLastApiResponse({
        endpoint: 'POST /tenants',
        ...res
      });
      if (res.error) {
        setError(res.error);
        setIsSaving(false);
        return;
      }
      setSuccess('Tenant creado exitosamente');
    } else if (selectedTenant) {
      const res = await updateTenant(selectedTenant.tenant_id, formData, token);
      setLastApiResponse({
        endpoint: `PUT /tenants/${selectedTenant.tenant_id}`,
        ...res
      });
      if (res.error) {
        setError(res.error);
        setIsSaving(false);
        return;
      }
      setSuccess('Tenant actualizado');
    }
    setIsSaving(false);
    setView('list');
  };
  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    if (!window.confirm(`¿Estás seguro de eliminar "${selectedTenant.name}"? Esta acción no se puede deshacer.`)) return;
    setIsSaving(true);
    const res = await deleteTenant(selectedTenant.tenant_id, token);
    setLastApiResponse({
      endpoint: `DELETE /tenants/${selectedTenant.tenant_id}`,
      ...res
    });
    if (res.error) {
      setError(res.error);
    } else {
      setSuccess('Tenant eliminado');
      setView('list');
    }
    setIsSaving(false);
  };
  const handleSaveSecret = async () => {
    if (!selectedTenant || !secretForm.service || !getSecretFieldsFilled()) return;
    setIsSaving(true);
    setError(null);
    try {
      const credsObj = buildCredentialsJson();
      const res = await createOrUpdateSecret(selectedTenant.tenant_id, secretForm.service, credsObj, token);
      setLastApiResponse({
        endpoint: `POST /tenants/${selectedTenant.tenant_id}/secrets/${secretForm.service}`,
        ...res
      });
      if (res.error) {
        setError(res.error);
      } else {
        const data = await getTenantWithSecrets(selectedTenant.tenant_id, token);
        setTenantSecrets(data.secrets || []);
        setSecretForm({
          service: '',
          fields: {}
        });
        setSuccess(`Secret "${secretForm.service}" guardado`);
      }
    } catch (err: any) {
      setError('JSON inválido: ' + err.message);
    }
    setIsSaving(false);
  };
  const handleDeleteSecret = async (service: string) => {
    if (!selectedTenant || !window.confirm(`¿Eliminar secret de ${service}?`)) return;
    setIsSaving(true);
    const res = await deleteSecret(selectedTenant.tenant_id, service, token);
    setLastApiResponse({
      endpoint: `DELETE /tenants/${selectedTenant.tenant_id}/secrets/${service}`,
      ...res
    });
    if (res.error) {
      setError(res.error);
    } else {
      const data = await getTenantWithSecrets(selectedTenant.tenant_id, token);
      setTenantSecrets(data.secrets || []);
      setSuccess(`Secret "${service}" eliminado`);
    }
    setIsSaving(false);
  };
  // Filter tenants by search
  const filteredTenants = tenants.filter(t => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return t.name?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q) || t.industry?.toLowerCase().includes(q);
  });
  return <div className="min-h-screen bg-slate-950 flex text-slate-200 font-sans selection:bg-indigo-500/30" data-id="element-6948">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col shrink-0" data-id="element-6949">
        <div className="p-6 border-b border-slate-800" data-id="element-6950">
          <WorkiumLogo size="md" data-id="element-6951" />
          <Badge variant="error" className="mt-3" data-id="element-6952">
            Superadmin Console
          </Badge>
        </div>
        <nav className="flex-1 p-4 space-y-2" data-id="element-6953">
          <button onClick={() => {
          setActiveTab('tenants');
          setView('list');
          setError(null);
        }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'tenants' ? 'bg-indigo-500/10 text-indigo-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`} data-id="element-6954">
            <Building2 size={18} data-id="element-6955" />
            Tenants
            {tenants.length > 0 && <span className="ml-auto text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full" data-id="element-6956">
                {tenants.length}
              </span>}
          </button>
          <button onClick={() => {
          setActiveTab('health');
          setError(null);
        }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'health' ? 'bg-indigo-500/10 text-indigo-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`} data-id="element-6957">
            <Activity size={18} data-id="element-6958" />
            System Health
          </button>
        </nav>
        <div className="p-4 border-t border-slate-800 space-y-3" data-id="element-6959">
          <div className="flex items-center gap-2 px-1" data-id="element-6960">
            <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-[10px] font-bold text-white" data-id="element-6961">
              SA
            </div>
            <span className="text-xs text-slate-400 truncate" data-id="element-6962">{email}</span>
          </div>
          <button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-500 hover:text-red-400 hover:bg-red-400/5 transition-colors" data-id="element-6963">
            <LogOut size={14} data-id="element-6964" />
            Cerrar sesión
          </button>
          <p className="text-[10px] text-slate-600 font-mono" data-id="element-6965">
            Workium AI Internal Tools
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-6966">
        {/* Header */}
        <header className="h-16 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between px-8 shrink-0" data-id="element-6967">
          <h1 className="text-lg font-semibold text-slate-100" data-id="element-6968">
            {activeTab === 'tenants' ? view === 'detail' ? isNewTenant ? 'Nuevo Tenant' : selectedTenant?.name || 'Detalle' : 'Gestión de Tenants' : 'System Health'}
          </h1>
          <div className="flex items-center gap-3" data-id="element-6969">
            <button onClick={() => setShowDebug(!showDebug)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${showDebug ? 'bg-amber-500/10 text-amber-400' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'}`} data-id="element-6970">
              <Bug size={14} data-id="element-6971" />
              Debug
            </button>
            <Badge variant="error" className="text-[10px]" data-id="element-6972">
              SUPERADMIN
            </Badge>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-8" data-id="element-6973">
          {/* Debug Panel */}
          {showDebug && <div className="mb-6 bg-amber-500/5 border border-amber-500/20 rounded-xl overflow-hidden" data-id="element-6974">
              <button onClick={() => setShowDebug(!showDebug)} className="w-full px-4 py-3 flex items-center justify-between text-amber-400 text-sm font-medium" data-id="element-6975">
                <span className="flex items-center gap-2" data-id="element-6976">
                  <Bug size={16} data-id="element-6977" />
                  API Debug — Última respuesta
                </span>
                {showDebug ? <ChevronUp size={16} data-id="element-6978" /> : <ChevronDown size={16} data-id="element-6979" />}
              </button>
              <div className="px-4 pb-4 space-y-2" data-id="element-6980">
                <div className="flex gap-2 text-xs" data-id="element-6981">
                  <span className="text-slate-500" data-id="element-6982">
                    Token (primeros 20 chars):
                  </span>
                  <code className="text-amber-400 bg-slate-950 px-2 py-0.5 rounded" data-id="element-6983">
                    {token.substring(0, 20)}...
                  </code>
                </div>
                <div className="flex gap-2 text-xs" data-id="element-6984">
                  <span className="text-slate-500" data-id="element-6985">Tenants en estado:</span>
                  <code className="text-amber-400 bg-slate-950 px-2 py-0.5 rounded" data-id="element-6986">
                    {tenants.length} items
                  </code>
                </div>
                {lastApiResponse && <pre className="text-xs font-mono text-slate-300 bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-auto max-h-64" data-id="element-6987">
                    {JSON.stringify(lastApiResponse, null, 2)}
                  </pre>}
                {!lastApiResponse && <p className="text-xs text-slate-500 italic" data-id="element-6988">
                    No hay respuestas aún. Navegá o recargá para ver datos.
                  </p>}
              </div>
            </div>}

          {/* Error banner */}
          {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-6989">
              <AlertCircle size={20} className="shrink-0 mt-0.5" data-id="element-6990" />
              <div className="flex-1" data-id="element-6991">
                <p className="text-sm" data-id="element-6992">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-400/50 hover:text-red-400" data-id="element-6993">
                <XCircle size={16} data-id="element-6994" />
              </button>
            </div>}

          {/* Success banner */}
          {success && <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3 text-emerald-400" data-id="element-6995">
              <CheckCircle2 size={20} data-id="element-6996" />
              <p className="text-sm" data-id="element-6997">{success}</p>
            </div>}

          {/* ═══ HEALTH TAB ═══ */}
          {activeTab === 'health' ? <div className="max-w-3xl space-y-6" data-id="element-6998">
              <div className="flex items-center justify-between" data-id="element-6999">
                <h2 className="text-xl font-bold text-slate-100" data-id="element-7000">
                  Estado del Sistema
                </h2>
                <Button variant="secondary" size="sm" onClick={fetchHealth} isLoading={healthLoading} className="gap-2" data-id="element-7001">
                  <RefreshCw size={14} data-id="element-7002" />
                  Verificar
                </Button>
              </div>

              <Card data-id="element-7003">
                <div className="p-6 flex items-center gap-4" data-id="element-7004">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${health?.ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`} data-id="element-7005">
                    <Server size={24} data-id="element-7006" />
                  </div>
                  <div className="flex-1" data-id="element-7007">
                    <h3 className="text-lg font-semibold text-slate-100" data-id="element-7008">
                      Tenant API Gateway
                    </h3>
                    <p className="text-sm text-slate-400" data-id="element-7009">
                      {health ? health.ok ? 'Todos los endpoints responden correctamente.' : `Error: ${health.error || 'No responde'}` : 'No verificado aún. Hacé clic en "Verificar".'}
                    </p>
                    {health?.checkedAt && <p className="text-xs text-slate-600 mt-1" data-id="element-7010">
                        Última verificación: {health.checkedAt}
                      </p>}
                  </div>
                  <Badge variant={health?.ok ? 'success' : health ? 'error' : 'outline'} data-id="element-7011">
                    {health?.ok ? 'Operativo' : health ? 'Error' : 'Pendiente'}
                  </Badge>
                </div>
              </Card>

              {health?.data && <Card data-id="element-7012">
                  <div className="p-6" data-id="element-7013">
                    <h3 className="text-sm font-medium text-slate-400 mb-3" data-id="element-7014">
                      Respuesta del servidor
                    </h3>
                    <pre className="text-xs font-mono text-slate-300 bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-auto max-h-48" data-id="element-7015">
                      {JSON.stringify(health.data, null, 2)}
                    </pre>
                  </div>
                </Card>}
            </div> : view === 'list' ? <div className="space-y-6" data-id="element-7016">
              <div className="flex items-center justify-between gap-4" data-id="element-7017">
                <div className="relative flex-1 max-w-md" data-id="element-7018">
                  <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-7019" />
                  <input type="text" placeholder="Buscar por nombre o email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50" data-id="element-7020" />
                </div>
                <div className="flex items-center gap-3" data-id="element-7021">
                  <Button variant="secondary" size="sm" onClick={fetchTenants} isLoading={isLoading} className="gap-2" data-id="element-7022">
                    <RefreshCw size={14} data-id="element-7023" />
                  </Button>
                  <Button onClick={handleCreateNew} className="gap-2" data-id="element-7024">
                    <Plus size={16} data-id="element-7025" />
                    Nuevo Tenant
                  </Button>
                </div>
              </div>

              {isLoading ? <div className="flex justify-center py-20" data-id="element-7026">
                  <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" data-id="element-7027" />
                </div> : <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-7028">
                  <table className="w-full text-left text-sm" data-id="element-7029">
                    <thead className="bg-slate-950 border-b border-slate-800 text-slate-400" data-id="element-7030">
                      <tr data-id="element-7031">
                        <th className="px-6 py-4 font-medium" data-id="element-7032">Nombre</th>
                        <th className="px-6 py-4 font-medium" data-id="element-7033">Email Admin</th>
                        <th className="px-6 py-4 font-medium" data-id="element-7034">Industria</th>
                        <th className="px-6 py-4 font-medium" data-id="element-7035">Plan</th>
                        <th className="px-6 py-4 font-medium" data-id="element-7036">Estado</th>
                        <th className="px-6 py-4 font-medium text-right" data-id="element-7037">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800" data-id="element-7038">
                      {filteredTenants.map(tenant => <tr key={tenant.tenant_id} className="hover:bg-slate-800/50 transition-colors cursor-pointer" onClick={() => handleViewTenant(tenant)} data-id="element-7039">
                          <td className="px-6 py-4 font-medium text-slate-200" data-id="element-7040">
                            {tenant.name}
                          </td>
                          <td className="px-6 py-4 text-slate-400" data-id="element-7041">
                            {tenant.email}
                          </td>
                          <td className="px-6 py-4 text-slate-400" data-id="element-7042">
                            {tenant.industry || '—'}
                          </td>
                          <td className="px-6 py-4" data-id="element-7043">
                            <Badge variant={tenant.active_plan === 'Pro' ? 'success' : tenant.active_plan === 'Enterprise' ? 'warning' : 'default'} data-id="element-7044">
                              {tenant.active_plan || 'Free'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4" data-id="element-7045">
                            <Badge variant={tenant.status === 'active' ? 'success' : tenant.status === 'deleted' ? 'error' : 'outline'} data-id="element-7046">
                              {tenant.status || 'active'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-7047">
                            <Button variant="ghost" size="sm" onClick={e => {
                      e.stopPropagation();
                      handleViewTenant(tenant);
                    }} data-id="element-7048">
                              Gestionar
                            </Button>
                          </td>
                        </tr>)}
                      {filteredTenants.length === 0 && !isLoading && <tr data-id="element-7049">
                          <td colSpan={6} className="px-6 py-16 text-center text-slate-500" data-id="element-7050">
                            <Building2 size={32} className="mx-auto mb-3 opacity-30" data-id="element-7051" />
                            <p className="text-sm" data-id="element-7052">
                              {searchQuery ? `No se encontraron tenants para "${searchQuery}"` : 'No hay tenants. Creá uno para empezar.'}
                            </p>
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>}
            </div> : <div className="max-w-4xl space-y-8 pb-20" data-id="element-7053">
              <div className="flex items-center justify-between" data-id="element-7054">
                <button onClick={() => {
              setView('list');
              setError(null);
            }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors" data-id="element-7055">
                  <ArrowLeft size={16} data-id="element-7056" />
                  Volver a Tenants
                </button>
                <div className="flex items-center gap-3" data-id="element-7057">
                  {!isNewTenant && <Button variant="danger" size="sm" onClick={handleDeleteTenant} isLoading={isSaving} data-id="element-7058">
                      <Trash2 size={16} className="mr-2" data-id="element-7059" />
                      Eliminar
                    </Button>}
                  <Button onClick={handleSaveTenant} isLoading={isSaving} className="gap-2" data-id="element-7060">
                    <Save size={16} data-id="element-7061" />
                    {isNewTenant ? 'Crear Tenant' : 'Guardar Cambios'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-7062">
                {/* General Info */}
                <div className="space-y-6" data-id="element-7063">
                  <h2 className="text-xl font-bold text-slate-100" data-id="element-7064">
                    Información General
                  </h2>
                  <Card data-id="element-7065">
                    <div className="p-6 space-y-4" data-id="element-7066">
                      <Input label="Nombre del Tenant" value={formData.name || ''} onChange={v => setFormData({
                    ...formData,
                    name: v
                  })} placeholder="Ej. Acme Corp" data-id="element-7067" />
                      <Input label="Email del Admin" value={formData.email || ''} onChange={v => setFormData({
                    ...formData,
                    email: v
                  })} placeholder="admin@acme.com" data-id="element-7068" />
                      <div className="grid grid-cols-2 gap-4" data-id="element-7069">
                        <div data-id="element-7070">
                          <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1" data-id="element-7071">
                            Industria
                          </label>
                          <select className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={formData.industry || ''} onChange={e => setFormData({
                        ...formData,
                        industry: e.target.value
                      })} data-id="element-7072">
                            <option value="" data-id="element-7073">Seleccionar...</option>
                            <option value="SaaS" data-id="element-7074">SaaS</option>
                            <option value="Fintech" data-id="element-7075">Fintech</option>
                            <option value="Insurance" data-id="element-7076">Seguros</option>
                            <option value="Healthcare" data-id="element-7077">Salud</option>
                            <option value="E-commerce" data-id="element-7078">E-commerce</option>
                            <option value="Other" data-id="element-7079">Otra</option>
                          </select>
                        </div>
                        <div data-id="element-7080">
                          <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1" data-id="element-7081">
                            Plan
                          </label>
                          <select className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={formData.active_plan || 'Free'} onChange={e => setFormData({
                        ...formData,
                        active_plan: e.target.value
                      })} data-id="element-7082">
                            <option value="Free" data-id="element-7083">Free</option>
                            <option value="Pro" data-id="element-7084">Pro</option>
                            <option value="Enterprise" data-id="element-7085">Enterprise</option>
                          </select>
                        </div>
                      </div>

                      {!isNewTenant && selectedTenant && <div className="pt-4 border-t border-slate-800 space-y-2" data-id="element-7086">
                          <p className="text-xs text-slate-500" data-id="element-7087">
                            <span className="text-slate-600" data-id="element-7088">ID:</span>{' '}
                            <code className="bg-slate-950 px-2 py-0.5 rounded text-slate-400" data-id="element-7089">
                              {selectedTenant.tenant_id}
                            </code>
                          </p>
                          {selectedTenant.created_at && <p className="text-xs text-slate-500" data-id="element-7090">
                              Creado:{' '}
                              {new Date(selectedTenant.created_at).toLocaleDateString('es-AR')}
                            </p>}
                        </div>}
                    </div>
                  </Card>
                </div>

                {/* Secrets Management */}
                <div className="space-y-6" data-id="element-7091">
                  <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2" data-id="element-7092">
                    <ShieldAlert size={20} className="text-indigo-400" data-id="element-7093" />
                    Servicios Conectados
                  </h2>

                  {isNewTenant ? <Card data-id="element-7094">
                      <div className="p-8 text-center text-slate-500" data-id="element-7095">
                        <Key size={32} className="mx-auto mb-3 opacity-50" data-id="element-7096" />
                        <p className="text-sm" data-id="element-7097">
                          Guardá el tenant primero para gestionar secrets.
                        </p>
                      </div>
                    </Card> : <div className="space-y-4" data-id="element-7098">
                      {tenantSecrets.length === 0 && <Card data-id="element-7099">
                          <div className="p-6 text-center text-slate-500" data-id="element-7100">
                            <Key size={24} className="mx-auto mb-2 opacity-30" data-id="element-7101" />
                            <p className="text-sm" data-id="element-7102">
                              Sin servicios configurados.
                            </p>
                          </div>
                        </Card>}

                      {tenantSecrets.map(secret => <Card key={secret.service} className="border-indigo-500/20 bg-indigo-500/5" data-id="element-7103">
                          <div className="p-4 flex items-center justify-between" data-id="element-7104">
                            <div className="flex items-center gap-3" data-id="element-7105">
                              <div className="w-8 h-8 rounded bg-indigo-950 flex items-center justify-center text-indigo-400" data-id="element-7106">
                                <Key size={16} data-id="element-7107" />
                              </div>
                              <div data-id="element-7108">
                                <p className="text-sm font-semibold text-slate-200 capitalize" data-id="element-7109">
                                  {secret.service}
                                </p>
                                <p className="text-xs text-slate-500" data-id="element-7110">
                                  {secret.updated_at ? `Actualizado: ${new Date(secret.updated_at).toLocaleDateString('es-AR')}` : 'Configurado'}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteSecret(secret.service)} className="text-red-400 hover:text-red-300 hover:bg-red-400/10" data-id="element-7111">
                              <Trash2 size={16} data-id="element-7112" />
                            </Button>
                          </div>
                        </Card>)}

                      {/* Add New Secret */}
                      <Card data-id="element-7113">
                        <div className="p-5 space-y-4" data-id="element-7114">
                          <h3 className="text-sm font-medium text-slate-300" data-id="element-7115">
                            Agregar / Actualizar Secret
                          </h3>
                          <div data-id="element-7116">
                            <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1" data-id="element-7117">
                              Servicio
                            </label>
                            <select className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={secretForm.service} onChange={e => handleServiceChange(e.target.value)} data-id="element-7118">
                              <option value="" data-id="element-7119">Seleccionar servicio...</option>
                              <option value="jira" data-id="element-7120">Jira</option>
                              <option value="confluence" data-id="element-7121">Confluence</option>
                              <option value="slack" data-id="element-7122">Slack</option>
                              <option value="github" data-id="element-7123">GitHub</option>
                              <option value="notion" data-id="element-7124">Notion</option>
                              <option value="google" data-id="element-7125">Google</option>
                            </select>
                          </div>

                          {/* Dynamic fields based on selected service */}
                          {secretForm.service && serviceFieldDefs[secretForm.service] && <div className="space-y-3 pt-1" data-id="element-7126">
                                {serviceFieldDefs[secretForm.service].map(field => <div key={field.key} data-id="element-7127">
                                      <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1" data-id="element-7128">
                                        {field.label}
                                      </label>
                                      <input type={field.type || 'text'} value={secretForm.fields[field.key] || ''} onChange={e => handleSecretFieldChange(field.key, e.target.value)} placeholder={field.placeholder} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-7129" />
                                    </div>)}
                              </div>}

                          {!secretForm.service && <p className="text-xs text-slate-500 text-center py-3" data-id="element-7130">
                              Seleccioná un servicio para ver los campos
                              requeridos.
                            </p>}

                          <div className="flex gap-2 pt-1" data-id="element-7131">
                            <Button variant="secondary" className="flex-1" onClick={handleSaveSecret} isLoading={isSaving} disabled={!secretForm.service || !getSecretFieldsFilled()} data-id="element-7132">
                              Guardar Secret
                            </Button>
                            {secretForm.service && Object.values(secretForm.fields).some(v => v) && <button onClick={downloadSecretJson} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-xs text-slate-300 transition-colors flex items-center gap-1.5 shrink-0" title="Descargar como JSON" data-id="element-7133">
                                  <Save size={14} data-id="element-7134" />
                                  JSON
                                </button>}
                          </div>
                        </div>
                      </Card>
                    </div>}
                </div>
              </div>
            </div>}
        </main>
      </div>
    </div>;
}